import { ITaskUpdatePayload } from "@/src/api/tasksPageRequests";
import { IApplicantUpdatePayload } from "@/src/types/applicant";
import {
  AOP_APPROVE_APPLICANT_FIELD_MAP,
  AOP_APPROVE_TASK_FIELD_MAP,
} from "@/src/constants/AdmissionOffice/admissionTaskPrefillMappings";
import type { AdmissionTaskPrefillFormBaseline } from "@/src/hooks/useAdmissionTaskPrefill";

const normalizeComparableString = (value: string): string =>
  value
    .replace(/[\u2018\u2019\u201B\u2032]/g, "'")
    .replace(/\u00A0/g, " ")
    .trim();

const readComparableValue = (value: unknown, transform?: (value: unknown) => string): string => {
  const raw = transform ? transform(value) : value == null ? "" : String(value);
  return normalizeComparableString(raw);
};

const toFormBaseline = (values: Record<string, unknown>): AdmissionTaskPrefillFormBaseline => {
  const next: AdmissionTaskPrefillFormBaseline = {};
  Object.entries(values).forEach(([key, value]) => {
    next[key] = value == null ? "" : String(value);
  });
  return next;
};

export interface ApproveFieldChange {
  formField: string;
  apiKey: string;
  from: string;
  to: string;
}

export interface BuildApproveUpdatesFromFormDiffParams {
  formBaseline: AdmissionTaskPrefillFormBaseline;
  formValues: Record<string, unknown>;
  attachedFile: string;
  nextStatus: string;
}

export interface BuildApproveUpdatesFromFormDiffResult {
  applicantPayload: IApplicantUpdatePayload;
  taskPayload: ITaskUpdatePayload;
  applicantChanges: ApproveFieldChange[];
  taskFormChanges: ApproveFieldChange[];
}

const collectChangedPayload = <T extends object>(
  fieldMap: ReadonlyArray<{ formField: string; apiKey: keyof T; transform?: (value: unknown) => string }>,
  formBaseline: AdmissionTaskPrefillFormBaseline,
  formValues: Record<string, unknown>,
  changes: ApproveFieldChange[],
): Partial<T> => {
  const payload: Partial<T> = {};

  fieldMap.forEach(({ formField, apiKey, transform }) => {
    const from = readComparableValue(formBaseline[formField], transform);
    const to = readComparableValue(formValues[formField], transform);
    if (from === to) return;

    changes.push({ formField, apiKey: String(apiKey), from, to });
    payload[apiKey] = to as T[keyof T];
  });

  return payload;
};

/**
 * Compares current AOP form values with the prefill baseline.
 * Builds two partial payloads keyed by PUT /applicants and PUT /tasks/update schemas.
 */
export const buildApproveUpdatesFromFormDiff = ({
  formBaseline,
  formValues,
  attachedFile,
  nextStatus,
}: BuildApproveUpdatesFromFormDiffParams): BuildApproveUpdatesFromFormDiffResult => {
  const applicantChanges: ApproveFieldChange[] = [];
  const taskFormChanges: ApproveFieldChange[] = [];
  const normalizedBaseline = toFormBaseline(formBaseline);
  const normalizedValues = toFormBaseline(formValues);

  const applicantPayload = collectChangedPayload<IApplicantUpdatePayload>(
    AOP_APPROVE_APPLICANT_FIELD_MAP,
    normalizedBaseline,
    normalizedValues,
    applicantChanges,
  ) as IApplicantUpdatePayload;

  const taskFieldChanges = collectChangedPayload<ITaskUpdatePayload>(
    AOP_APPROVE_TASK_FIELD_MAP,
    normalizedBaseline,
    normalizedValues,
    taskFormChanges,
  );

  const taskPayload: ITaskUpdatePayload = {
    status: nextStatus,
    attached_file: attachedFile,
    ...taskFieldChanges,
  };

  return { applicantPayload, taskPayload, applicantChanges, taskFormChanges };
};

export const logApproveChangedFields = ({
  applicantId,
  taskId,
  applicantPayload,
  taskPayload,
  applicantChanges,
  taskFormChanges,
  nextStatus,
  attachedFile,
}: {
  applicantId: number | string;
  taskId: string;
  applicantPayload: IApplicantUpdatePayload;
  taskPayload: ITaskUpdatePayload;
  applicantChanges: ApproveFieldChange[];
  taskFormChanges: ApproveFieldChange[];
  nextStatus: string;
  attachedFile: string;
}): void => {
  console.groupCollapsed("[AdmissionOffice Approve] Changed fields");

  console.groupCollapsed(`PUT /applicants/${applicantId}`);
  if (applicantChanges.length === 0) {
    console.log("No form fields changed — request will not be sent.");
  } else {
    applicantChanges.forEach(({ formField, apiKey, from, to }) => {
      console.log(`${formField} → ${apiKey}: "${from}" → "${to}"`);
    });
    console.log("Payload:", applicantPayload);
  }
  console.groupEnd();

  console.groupCollapsed(`PUT /tasks/update/${taskId}`);
  if (taskFormChanges.length === 0) {
    console.log("No form fields changed.");
  } else {
    taskFormChanges.forEach(({ formField, apiKey, from, to }) => {
      console.log(`${formField} → ${apiKey}: "${from}" → "${to}"`);
    });
  }
  console.log(`status (always on approve): "${nextStatus}"`);
  console.log(`attached_file (always on approve): "${attachedFile}"`);
  console.log("Payload:", taskPayload);
  console.groupEnd();

  console.groupEnd();
};
